// src/pages/People/AddPersonPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type { PersonModelInterface } from "@/models/personModel";
import { usePeople } from "@/context/PeopleContext";
import { useAuth } from "@/context/AuthContext";

import PersonInfoForms from "@/components/Forms/PersonInfo/PersonInfoForms";
import PhoneInfoForms from "@/components/Forms/PhoneInfo/PhoneInfoForms";
import LoanCalculationForms from "@/components/Forms/LoanCalculation/LoanCalculationForms";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

import {
    ArrowLeft,
    User,
    Smartphone,
    Calculator,
    X,
    CheckCircle2,
    Sparkles,
    Save,
} from "lucide-react";

const localStorageKey = "addPersonFormData";

const steps = [
    { key: "info", title: "Личная информация", icon: User },
    { key: "phone", title: "Телефон", icon: Smartphone },
    { key: "credit", title: "Расчёт кредита", icon: Calculator },
] as const;

function buildFullName(data: PersonModelInterface) {
    const first = data?.detailInfo?.firstName || "";
    const second = data?.detailInfo?.secondName || "";
    const last = data?.detailInfo?.lastName || "";
    return `${first} ${second}${last ? ` ${last}` : ""}`.trim();
}

function safeJsonParse<T>(raw: string | null): T | null {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

export default function AddPersonPage() {
    const navigate = useNavigate();
    const { addPerson } = usePeople();
    const { user } = useAuth();

    const methods = useForm<PersonModelInterface>({
        mode: "onChange",
        defaultValues: {
            name: "",
            detailInfo: {
                firstName: "",
                secondName: "",
                lastName: "",
                phoneNumber: "",
                individualNumber: "",
                idPassport: "",
                address: "",
                city: "",
                placeOfWork: "",
            },
            phone: {
                phoneModel: "",
                imei: "",
                icloud: "",
                password: "",
                costPrice: 0,
                condition: "",
                registrationNumber: "",
            },
            status: "",
            creditor: "",
            creditDetail: {
                phoneAmount: 0,
                downPayment: 0,
                percent: 30,
                term: 3,
                monthlyPayment: 0,
                total: 0,
                closingDay: "",
                releaseDay: "",
            },
            hasPayment: false,
        },
    });

    const { handleSubmit, trigger, reset, watch } = methods;

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const saved = safeJsonParse<PersonModelInterface>(
            localStorage.getItem(localStorageKey)
        );
        if (saved) reset(saved);
    }, [reset]);

    useEffect(() => {
        const sub = watch((data) => {
            localStorage.setItem(localStorageKey, JSON.stringify(data));
        });
        return () => sub.unsubscribe();
    }, [watch]);

    const stepFields = (stepIndex: number) => {
        if (stepIndex === 0) {
            return [
                "detailInfo.firstName",
                "detailInfo.secondName",
                "detailInfo.lastName",
                "detailInfo.phoneNumber",
                "detailInfo.individualNumber",
                "detailInfo.idPassport",
                "detailInfo.address",
                "detailInfo.city",
                "detailInfo.placeOfWork",
            ] as const;
        }
        if (stepIndex === 1) {
            return [
                "phone.phoneModel",
                "phone.imei",
                "phone.icloud",
                "phone.password",
                "phone.condition",
                "phone.registrationNumber",
            ] as const;
        }
        return [
            "creditDetail.phoneAmount",
            "creditDetail.downPayment",
            "creditDetail.releaseDay",
        ] as const;
    };

    const progress = useMemo(() => {
        const total = steps.length;
        const done = activeStep + 1;
        return Math.round((done / total) * 100);
    }, [activeStep]);

    const onClose = () => {
        localStorage.removeItem(localStorageKey);
        navigate("/people");
    };

    const handleBack = () => setActiveStep((p) => Math.max(0, p - 1));

    const handleNext = async () => {
        const fields = stepFields(activeStep);
        const ok = await trigger(fields as any, { shouldFocus: true });
        if (ok) setActiveStep((p) => Math.min(steps.length - 1, p + 1));
    };

    const jumpTo = async (idx: number) => {
        if (idx === activeStep || loading) return;

        if (idx > activeStep) {
            const ok = await trigger(stepFields(activeStep) as any, { shouldFocus: true });
            if (!ok) return;
        }
        setActiveStep(idx);
    };

    const onSubmit: SubmitHandler<PersonModelInterface> = async (data) => {
        try {
            setLoading(true);
            localStorage.removeItem(localStorageKey);

            const fullName = buildFullName(data);
            const creditorName = (user?.displayName as string) || "Менеджер";

            const personData: PersonModelInterface = {
                ...data,
                name: fullName,
                status: "new",
                creditor: creditorName,
                hasPayment: false,
            };

            await addPerson(personData);
            reset();
            toast.success("Клиент успешно добавлен!");
            navigate("/people");
        } catch (e) {
            console.error(e);
            toast.error("Ошибка при сохранении клиента");
        } finally {
            setLoading(false);
        }
    };

    const CurrentIcon = steps[activeStep].icon;

    return (
        <div className="mx-auto w-full max-w-5xl px-4 py-6">
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Card className="overflow-hidden">
                        {/* Top header */}
                        <CardHeader className="pb-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl border bg-muted/30">
                                            <Sparkles className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <div className="space-y-0.5">
                                            <CardTitle className="text-xl">Регистрация клиента</CardTitle>
                                            <div className="text-sm text-muted-foreground">
                                                Черновик сохраняется автоматически
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate(-1)}
                                        disabled={loading}
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Назад
                                    </Button>
                                    <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                                        <X className="h-4 w-4 mr-2" />
                                        Отмена
                                    </Button>
                                </div>
                            </div>

                            <Separator className="my-4" />

                            {/* Step tabs + progress */}
                            <div className="space-y-3">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-muted/30">
                                            <CurrentIcon className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <div className="text-sm font-medium">{steps[activeStep].title}</div>
                                        <Badge variant="secondary" className="ml-1">
                                            {progress}%
                                        </Badge>
                                    </div>

                                    <div className="text-xs text-muted-foreground">
                                        Шаг {activeStep + 1} из {steps.length}
                                    </div>
                                </div>

                                <Progress value={progress} />

                                {/* Tabs */}
                                <div className="grid grid-cols-3 gap-2">
                                    {steps.map((s, idx) => {
                                        const Icon = s.icon;
                                        const isActive = idx === activeStep;
                                        const isDone = idx < activeStep;

                                        return (
                                            <button
                                                key={s.key}
                                                type="button"
                                                onClick={() => jumpTo(idx)}
                                                className={[
                                                    "flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm transition",
                                                    isActive
                                                        ? "bg-muted/30 border-foreground/10"
                                                        : "hover:bg-muted/20",
                                                ].join(" ")}
                                            >
                                                {isDone ? (
                                                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                                )}
                                                <span className="hidden sm:inline">{s.title}</span>
                                                <span className="sm:hidden">{idx + 1}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </CardHeader>

                        {/* Content */}
                        <CardContent className="space-y-4">
                            {activeStep === 0 ? <PersonInfoForms /> : null}
                            {activeStep === 1 ? <PhoneInfoForms /> : null}
                            {activeStep === 2 ? <LoanCalculationForms /> : null}

                            <Separator className="my-2" />

                            {/* Bottom actions */}
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleBack}
                                        disabled={loading || activeStep === 0}
                                        className="w-full sm:w-auto"
                                    >
                                        Назад
                                    </Button>

                                    {activeStep < steps.length - 1 ? (
                                        <Button
                                            type="button"
                                            onClick={handleNext}
                                            disabled={loading}
                                            className="w-full sm:w-auto"
                                        >
                                            Далее
                                        </Button>
                                    ) : (
                                        <Button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full sm:w-auto"
                                        >
                                            <Save className="h-4 w-4 mr-2" />
                                            {loading ? "Сохранение..." : "Сохранить"}
                                        </Button>
                                    )}
                                </div>

                                <div className="text-xs text-muted-foreground">
                                    Совет: можно переключаться по вкладкам сверху
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </form>
            </FormProvider>
        </div>
    );
}
